import { useState, useEffect, useMemo } from 'react';
import PeriodFilter, { usePeriodFilter } from '../components/PeriodFilter';
import { EMPTY_PERIOD } from '../components/common/ClearFiltersButton';
import MultiSelectFilter, { listValue, listParam } from '../components/common/MultiSelectFilter';
import FilterChips from '../components/common/FilterChips';
import SavedViews from '../components/common/SavedViews';
import { useNavigate } from 'react-router-dom';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import EntityLink from '../components/EntityLink';
import NoteText, { InvoiceNotes } from '../components/NoteText';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import { localDateStr } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import SortTh, { useSort } from '../components/common/SortableHeader';
import { useToast } from '../context/FeedbackContext';
// AP amount = vendor's billed hours x rate (2-Oct-26 rule); approved hours are kept as their own field.
const withBilledAmount = (f) => {
  if ((f.invoice_type || 'standard') !== 'standard') return f;
  const h = parseFloat(f.hours_billed), r = parseFloat(f.rate);
  return (h > 0 && r > 0) ? { ...f, amount_to_pay: (h * r).toFixed(2) } : f;
};


const INVOICE_TYPE_LABELS = { standard: 'Standard', passthrough: 'Allowance / Reimbursement' };

// Pay state comes from the server (utils/apPayability.js) - the same rules
// as the AP Payments Queue, so "Payable now" here is exactly the queue.
const PAY_STATES = [
  { value: 'payable', label: 'Payable now', variant: 'success' },
  { value: 'waiting_ar', label: 'Waiting on AR payment', variant: 'warning' },
  { value: 'waiting_hours', label: 'Waiting on more AR-approved hours', variant: 'warning' },
  { value: 'no_ar_match', label: 'No matching AR invoice', variant: 'danger' },
  { value: 'nothing_due', label: 'Nothing due now', variant: undefined },
  { value: 'legacy', label: 'AR paid before Mar 2026', variant: undefined },
  { value: 'paid', label: 'Paid', variant: undefined },
  { value: 'void', label: 'Void', variant: undefined },
];
const PAY_STATE_BY_VALUE = Object.fromEntries(PAY_STATES.map(p => [p.value, p]));

// Payment progress only (since the 2026-09-21 split, AR readiness is Pay state).
const AP_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

export default function APInvoicesPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState(false);
  const { period, setPeriod, filterByPeriod } = usePeriodFilter("period_start");
  const { get, set } = useUrlParams();
  // Multi-select filters: OR within a field, AND across fields. Old
  // single-value links (?status=all) still work - 'all' is ignored.
  const payFilter = listValue(get('pay', ''));
  const statusFilter = listValue(get('status', '')).filter(v => v !== 'all');
  const typeFilter = listValue(get('type', '')).filter(v => v !== 'all');
  const vendorFilter = listValue(get('vendor', ''));
  const contractorFilter = listValue(get('contractor', ''));
  const search = get('search', '');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [showImport, setShowImport] = useState(false);
  const [importingBankPayments, setImportingBankPayments] = useState(false);
  const [bankImportResult, setBankImportResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const { sortKey, sortDir, handleSort, sortData } = useSort('invoice_date', 'desc');
  const [workOrders, setWorkOrders] = useState([]);
  const [pos, setPos] = useState([]);
  const [form, setForm] = useState({ invoice_number: '', wo_id: '', po_id: '', vendor_id: '', invoice_date: '', period_start: '', period_end: '', hours_billed: '', hours_approved: '', rate: '', amount_to_pay: '', notes: '', invoice_type: 'standard', allowance_type: '', allowance_category: '', reference_note: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);

  const handleBatchPaymentAdvice = async () => {
    if (!selectedIds.length) return;
    setGeneratingAdvice(true);
    try {
      const response = await api.post('/payment-advice/batch', { invoiceIds: selectedIds }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      // Read the real filename (PA-<reference>.pdf) from the server's Content-Disposition
      // header instead of hardcoding a generic date-based name — avoids every batch on the
      // same day colliding under one filename regardless of vendor.
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', match ? match[1] : `payment-advice-batch-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSelectedIds([]);
    } catch (err) {
      // responseType 'blob' hides the server's JSON error - read it back out
      let msg = err.message || 'Unknown error';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      toast('Failed to generate Payment Advice: ' + msg, 'error');
    } finally {
      setGeneratingAdvice(false);
    }
  };

  const toggleSelect = (id, vendorId) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length > 0 && vendorId !== selectedVendorId) return [id];
      return [...prev, id];
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paged.length) {
      setSelectedIds([]);
    } else {
      const anchorVendorId = selectedVendorId ?? paged[0]?.vendor_id;
      setSelectedIds(paged.filter(inv => inv.vendor_id === anchorVendorId).map(inv => inv.id));
    }
  };

  function load() {
    setError(false);
    api.get('/ap-invoices').then(r => setInvoices(r.data)).catch(() => setError(true));
  }

  useEffect(() => {
    load();
    api.get('/work-orders?engagement_type=contract_placement').then(r => setWorkOrders(r.data));
    api.get('/purchase-orders').then(r => setPos(r.data));
  }, []);

  // POs filtered by selected WO and period overlap
  const filteredPOs = useMemo(() => {
    if (!form.wo_id) return [];
    return pos.filter(p => {
      if (p.wo_id !== Number(form.wo_id)) return false;
      if (!form.period_start || !form.period_end) return true;
      return p.period_start <= form.period_end && p.period_end >= form.period_start;
    });
  }, [pos, form.wo_id, form.period_start, form.period_end]);

  // Auto-calculate amount = billed hours x rate (2-Oct-26 rule). Approved
  // hours are recorded separately and never change the amount.
  useEffect(() => {
    const h = parseFloat(form.hours_billed);
    const r = parseFloat(form.rate);
    if (!isNaN(h) && !isNaN(r) && h > 0 && r > 0) {
      setForm(f => ({ ...f, amount_to_pay: (h * r).toFixed(2) }));
    }
  }, [form.hours_billed, form.rate]);

  // Split invoices (e.g. 2086 / 2086A: same base number, PO and period) are
  // one bill in two rows - badge them and keep them together in results.
  const splitKeyOf = i => `${String(i.invoice_number || '').trim().replace(/(\d)A$/i, '$1')}|${i.po_id || ''}|${i.period_start || ''}`;
  const splitGroups = useMemo(() => {
    const groups = new Map();
    for (const i of invoices || []) {
      const k = splitKeyOf(i);
      groups.set(k, (groups.get(k) || 0) + 1);
    }
    return groups;
  }, [invoices]);
  const isSplit = i => (splitGroups.get(splitKeyOf(i)) || 0) > 1;

  const filtered = useMemo(() => {
    if (!invoices) return [];
    const s = search.toLowerCase();
    const matches = i =>
      (!payFilter.length || payFilter.includes(i.pay_state)) &&
      (!statusFilter.length || statusFilter.includes(i.status)) &&
      (!typeFilter.length || typeFilter.includes(i.invoice_type || 'standard')) &&
      (!vendorFilter.length || vendorFilter.includes(String(i.vendor_id))) &&
      (!contractorFilter.length || contractorFilter.includes(String(i.contractor_id))) &&
      (!s || i.invoice_number.toLowerCase().includes(s) ||
        (i.vendor_name || '').toLowerCase().includes(s) ||
        (i.contractor_name || '').toLowerCase().includes(s));
    const direct = new Set(filterByPeriod(invoices.filter(matches)).map(i => i.id));
    // Pull in the other half of any matched split pair.
    const matchedSplitKeys = new Set(invoices.filter(i => direct.has(i.id) && isSplit(i)).map(splitKeyOf));
    return invoices.filter(i => direct.has(i.id) || (matchedSplitKeys.size && matchedSplitKeys.has(splitKeyOf(i)) && isSplit(i)));
  }, [invoices, payFilter.join(), statusFilter.join(), typeFilter.join(), vendorFilter.join(), contractorFilter.join(), search, period, filterByPeriod, splitGroups]);

  // Dropdown options (with counts over the whole list).
  const countBy = (keyFn) => {
    const m = new Map();
    for (const i of invoices || []) { const k = keyFn(i); if (k != null) m.set(k, (m.get(k) || 0) + 1); }
    return m;
  };
  const filterOptions = useMemo(() => {
    const payCounts = countBy(i => i.pay_state);
    const vendorNames = new Map((invoices || []).filter(i => i.vendor_id).map(i => [String(i.vendor_id), i.vendor_name || '—']));
    const contractorNames = new Map((invoices || []).filter(i => i.contractor_id).map(i => [String(i.contractor_id), i.contractor_name || '—']));
    const vendorCounts = countBy(i => i.vendor_id ? String(i.vendor_id) : null);
    const contractorCounts = countBy(i => i.contractor_id ? String(i.contractor_id) : null);
    const byName = (a, b) => a.label.localeCompare(b.label);
    return {
      pay: PAY_STATES.filter(p => payCounts.get(p.value)).map(p => ({ ...p, count: payCounts.get(p.value) })),
      status: AP_STATUSES.map(v => ({ value: v.value, label: v.label, count: countBy(i => i.status).get(v.value) || 0 })),
      type: Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({ value, label })),
      vendor: [...vendorNames].map(([value, label]) => ({ value, label, count: vendorCounts.get(value) })).sort(byName),
      contractor: [...contractorNames].map(([value, label]) => ({ value, label, count: contractorCounts.get(value) })).sort(byName),
    };
  }, [invoices]);

  const optionLabel = (field, value) => (filterOptions[field].find(o => o.value === value) || {}).label || value;
  const setList = (key, values) => set({ [key]: listParam(values), page: 1 });
  const FIELD_NAMES = { pay: 'Pay state', status: 'Status', type: 'Type', vendor: 'Vendor', contractor: 'Contractor' };
  const currentLists = { pay: payFilter, status: statusFilter, type: typeFilter, vendor: vendorFilter, contractor: contractorFilter };
  const chips = [
    ...Object.entries(currentLists).flatMap(([field, values]) => values.map(v => ({
      key: `${field}:${v}`,
      label: `${FIELD_NAMES[field]}: ${optionLabel(field, v)}`,
      onRemove: () => setList(field, values.filter(x => x !== v)),
    }))),
    ...(search ? [{ key: 'search', label: `Search: "${search}"`, onRemove: () => set({ search: '', page: 1 }) }] : []),
    ...(period.from || period.to ? [{ key: 'period', label: `Period: ${period.from || '…'} → ${period.to || '…'}`, onRemove: () => setPeriod(EMPTY_PERIOD) }] : []),
  ];
  const clearAllFilters = () => { set({ pay: '', status: '', type: '', vendor: '', contractor: '', search: '', page: 1 }); setPeriod(EMPTY_PERIOD); };
  const savedViewState = { pay: payFilter, status: statusFilter, type: typeFilter, vendor: vendorFilter, contractor: contractorFilter, search, period };
  const applySavedView = v => {
    set({
      pay: listParam(v.pay || []), status: listParam(v.status || []), type: listParam(v.type || []),
      vendor: listParam(v.vendor || []), contractor: listParam(v.contractor || []), search: v.search || '', page: 1,
    });
    setPeriod(v.period || EMPTY_PERIOD);
  };

  const sorted = useMemo(() => {
    return sortData(filtered, {
      invoice_number: (i) => i.invoice_number || "",
      vendor_name: (i) => i.vendor_name || "",
      contractor_name: (i) => i.contractor_name || "",
      invoice_date: (i) => i.invoice_date || "",
      period_start: (i) => i.period_start || "",
      period_end: (i) => i.period_end || "",
      amount_to_pay: (i) => i.amount_to_pay || 0,
      verified_paid: (i) => i.verified_paid || 0,
      status: (i) => i.status || "",
      pay_state: (i) => PAY_STATES.findIndex(p => p.value === i.pay_state),
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);
  // Payment Advice can only cover ONE vendor at a time (the PDF has a single
  // "TO" vendor block, and the server now hard-rejects mixed-vendor batches).
  // Selecting an invoice from a different vendor than what's already selected
  // starts a fresh selection with just that invoice, rather than silently
  // letting a mixed selection build up. Declared here (after `paged`/`sorted`
  // exist) to avoid a temporal-dead-zone crash. (2026-08-18)
  const selectedVendorId = selectedIds.length > 0
    ? (paged.find(inv => inv.id === selectedIds[0]) || sorted.find(inv => inv.id === selectedIds[0]))?.vendor_id
    : null;

  function selectWO(woId) {
    const wo = workOrders.find(w => w.id === Number(woId));
    setForm(f => ({ ...f, wo_id: woId, vendor_id: wo?.vendor_id || '', rate: wo?.buy_rate || '', po_id: '' }));
  }

  function selectPO(poId) {
    const po = pos.find(p => p.id === Number(poId));
    setForm(f => ({
      ...f, po_id: poId,
      rate: po?.buy_rate || f.rate,
    }));
  }

  async function createInvoice() {
    if (!form.invoice_number || !form.wo_id || !form.invoice_date) {
      setFormError('Work order, invoice date and invoice number are required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/ap-invoices', form);
      setForm({ invoice_number: '', wo_id: '', po_id: '', vendor_id: '', invoice_date: '', period_start: '', period_end: '', hours_billed: '', hours_approved: '', rate: '', amount_to_pay: '', notes: '', invoice_type: 'standard', allowance_type: '', allowance_category: '', reference_note: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleBankPaymentImport(file) {
    setImportingBankPayments(true);
    setBankImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/ap-invoices/import-bank-payments', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBankImportResult(r.data);
      load();
    } catch (e) {
      setBankImportResult({ message: e.response?.data?.error || 'Import failed', warnings: [] });
    } finally {
      setImportingBankPayments(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">AP Invoices</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => {
            downloadCsv('/ap-invoices/export', 'ap-invoices.csv', exportIdsParam(filtered, invoices))
              .catch(() => toast('Could not export CSV', 'error'));
          }}>↓ Export CSV</button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {importingBankPayments ? 'Importing…' : '↑ Import Bank Payment Report'}
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleBankPaymentImport(e.target.files[0])} />
          </label>
          <button className="btn btn-primary" onClick={() => {
            const today = localDateStr();
            setForm(f => ({ ...f, invoice_date: today }));
            setShowForm(true);
          }}>+ Create Invoice</button>
        </div>
      </div>

      {bankImportResult && (
        <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
          {bankImportResult.message}
          {bankImportResult.warnings && bankImportResult.warnings.length > 0 && (
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
              {bankImportResult.warnings.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
      {showImport && <CsvImportModal type="ap-invoices" onClose={() => setShowImport(false)} onImported={load} />}

      {showForm && (
        <div className="ar-invoice-form" style={{ background: 'var(--surface)', padding: 16, borderRadius: 'var(--radius)', marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Create AP Invoice</h3>
          {formError && <div style={{ color: 'var(--error)', marginBottom: 10, fontSize: 12 }}>{formError}</div>}

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>1. Invoice Type</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
            {[
              { v: 'standard', label: 'Standard', sub: 'Hourly billing' },
              { v: 'passthrough', label: 'Allowance / Reimbursement', sub: 'Expenses and allowances' },
            ].map(t => (
              <button key={t.v} type="button" onClick={() => setForm(f => ({
                ...f, invoice_type: t.v, hours_billed: '', hours_approved: '', rate: '', amount_to_pay: '',
                allowance_type: '', allowance_category: '', reference_note: '',
              }))} style={{
                padding: '10px 12px', textAlign: 'left', borderRadius: 'var(--radius)', cursor: 'pointer',
                border: form.invoice_type === t.v ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: form.invoice_type === t.v ? 'var(--info-bg)' : 'var(--card)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.sub}</div>
              </button>
            ))}
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>2. Invoice Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ gridColumn: 'span 3' }}>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Invoice Number (from vendor's own invoice)</label>
              <input type="text" value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="e.g. VND-2026-001" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Work Order</label>
              <select value={form.wo_id} onChange={e => selectWO(e.target.value)}>
                <option value="">Select Work Order</option>
                {workOrders.sort((a, b) => a.contractor_name.localeCompare(b.contractor_name)).map(w => (
                  <option key={w.id} value={w.id}>{w.contractor_name} - {w.outgoing_wo_number} ({w.client_wo_number}){w.status !== 'active' ? ' (closed)' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Outgoing WO #</label>
              <input type="text" disabled value={form.wo_id ? (workOrders.find(w => w.id === Number(form.wo_id))?.outgoing_wo_number || '') : ''} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
            </div>
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
            3. {form.invoice_type === 'passthrough' ? 'Expense Details' : 'Billing Period and Hours'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            {form.invoice_type === 'passthrough' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Type</label>
                  <select value={form.allowance_type} onChange={e => setForm(f => ({ ...f, allowance_type: e.target.value }))}>
                    <option value="">Select type</option>
                    <option value="allowance">Allowance</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Category</label>
                  <input type="text" placeholder="e.g. Shift Allowance, Phone, Taxi" value={form.allowance_category} onChange={e => setForm(f => ({ ...f, allowance_category: e.target.value }))} />
                </div>
                <div></div>
              </>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period Start</label>
              <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value, po_id: '' }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Period End</label>
              <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value, po_id: '' }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Purchase Order {form.period_start && form.period_end ? '(filtered for period above)' : '(enter period to filter)'}
              </label>
              <select value={form.po_id} onChange={e => selectPO(e.target.value)} disabled={!form.wo_id}>
                <option value="">{form.wo_id ? (filteredPOs.length ? 'Select PO' : 'No POs match this period') : 'Select Work Order first'}</option>
                {filteredPOs.map(p => (
                  <option key={p.id} value={p.id}>{p.po_number} ({p.period_start} → {p.period_end})</option>
                ))}
              </select>
            </div>
            {form.invoice_type === 'passthrough' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Reference Note (appears on invoice, e.g. "Manager email attached")</label>
                  <input type="text" placeholder="e.g. Manager email attached, Phone bill attached" value={form.reference_note} onChange={e => setForm(f => ({ ...f, reference_note: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount to Pay</label>
                  <input type="number" step="0.01" value={form.amount_to_pay} onChange={e => setForm(f => ({ ...f, amount_to_pay: e.target.value }))} />
                </div>
                <div></div>
              </>
            )}
            {form.invoice_type === 'standard' && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Buy Rate (read-only)</label>
                  <input type="text" disabled value={form.rate ? `$${form.rate}/hr` : ''} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Billed Hours (vendor invoice)</label>
                  <input type="number" value={form.hours_billed} onChange={e => setForm(f => ({ ...f, hours_billed: e.target.value }))} placeholder="e.g. 176" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Approved Hours (Hexaware)</label>
                  <input type="number" value={form.hours_approved} onChange={e => setForm(f => ({ ...f, hours_approved: e.target.value }))} placeholder="e.g. 168" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount to Pay (billed hours × rate)</label>
                  <input type="number" step="0.01" value={form.amount_to_pay} onChange={e => setForm(f => ({ ...f, amount_to_pay: e.target.value }))} placeholder="Auto-fills from billed hours × rate" />
                </div>
              </>
            )}
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>4. Notes</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows="2" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setFormError(''); }}>Cancel</button>
            <button className="btn btn-primary" onClick={createInvoice} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <PeriodFilter value={period} onChange={setPeriod} />
        <input type="text" className="filter-search" placeholder="Search invoice #, vendor or contractor…" value={search} onChange={e => set({ search: e.target.value, page: 1 })} />
        <MultiSelectFilter label="Pay state" options={filterOptions.pay} selected={payFilter} onChange={v => setList('pay', v)} />
        <MultiSelectFilter label="Status" options={filterOptions.status} selected={statusFilter} onChange={v => setList('status', v)} />
        <MultiSelectFilter label="Type" options={filterOptions.type} selected={typeFilter} onChange={v => setList('type', v)} />
        <MultiSelectFilter label="Vendor" options={filterOptions.vendor} selected={vendorFilter} onChange={v => setList('vendor', v)} />
        <MultiSelectFilter label="Contractor" options={filterOptions.contractor} selected={contractorFilter} onChange={v => setList('contractor', v)} />
        <SavedViews pageKey="ap_invoices" currentState={savedViewState} canSave={chips.length > 0} onApply={applySavedView} />
        {selectedIds.length > 0 && (
          <button
            className="btn btn-primary"
            onClick={handleBatchPaymentAdvice}
            disabled={generatingAdvice}
            style={{ background: 'var(--accent)', border: 'none', marginLeft: 'auto' }}
          >
            {generatingAdvice ? 'Generating…' : `📄 Payment Advice (${selectedIds.length})`}
          </button>
        )}
      </div>
      <FilterChips chips={chips} onClearAll={clearAllFilters} resultCount={filtered.length} totalCount={invoices?.length || 0} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && invoices === null && <StateBlock type="loading" />}
      {!error && invoices !== null && filtered.length === 0 && <StateBlock type="noResults" />}
      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}><input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === paged.length && paged.length > 0} /></th>
                <SortTh label="Invoice #" sortKey="invoice_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Vendor" sortKey="vendor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Contractor" sortKey="contractor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Date" sortKey="invoice_date" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Period Start" sortKey="period_start" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Period End" sortKey="period_end" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><th>Type</th><SortTh label="Amount" sortKey="amount_to_pay" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" /><SortTh label="Verified Paid" sortKey="verified_paid" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" /><SortTh label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} /><SortTh label="Pay State" sortKey="pay_state" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {paged.map(inv => (
                <tr key={inv.id} style={selectedIds.includes(inv.id) ? { background: 'var(--accent-subtle, #f0f4ff)' } : (selectedIds.length > 0 && !selectedIds.includes(inv.id) && inv.vendor_id !== selectedVendorId ? { opacity: 0.4 } : {})}>
                  <td style={{ width: 32 }}><input type="checkbox" checked={selectedIds.includes(inv.id)} disabled={selectedIds.length > 0 && !selectedIds.includes(inv.id) && inv.vendor_id !== selectedVendorId} onChange={() => toggleSelect(inv.id, inv.vendor_id)} /></td>
                  <td>
                    <button className="row-name-link" onClick={() => setDetailId(inv.id)}>{inv.invoice_number}</button>
                    {isSplit(inv) && <span className="split-badge" title="Split invoice - one bill across two rows (same base number, PO and period)">SPLIT</span>}
                  </td>
                  <td><EntityLink type="vendor" id={inv.vendor_id}>{inv.vendor_name}</EntityLink></td>
                  <td><EntityLink type="contractor" id={inv.contractor_id}>{inv.contractor_name}</EntityLink></td>
                  <td>{inv.invoice_date}</td>
                  <td>{inv.period_start}</td>
                  <td>{inv.period_end}</td>
                  <td><Badge variant={inv.invoice_type === 'passthrough' ? 'info' : undefined}>{INVOICE_TYPE_LABELS[inv.invoice_type] || INVOICE_TYPE_LABELS.standard}</Badge></td>
                  <td>${inv.amount_to_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td>${(inv.verified_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td><Badge>{inv.status}</Badge></td>
                  <td>
                    {inv.pay_state && (
                      <Badge variant={PAY_STATE_BY_VALUE[inv.pay_state]?.variant}>
                        {PAY_STATE_BY_VALUE[inv.pay_state]?.label || inv.pay_state}
                        {inv.pay_state === 'payable' && inv.payable_amount != null && ` · $${inv.payable_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={(p) => set({ page: p })} onPageSizeChange={(s) => set({ pageSize: s, page: 1 })} />
        </>
      )}

      {detailId && <APInvoiceDetailPanel invoiceId={detailId} onClose={() => setDetailId(null)} onChanged={load} pos={pos} />}
    </div>
  );
}

function APInvoiceDetailPanel({ invoiceId, onClose, onChanged, pos }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [showPayForm, setShowPayForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const EMPTY_PAY_FORM = { payment_date: '', cash_amount: '', epd_rate: '', epd_amount: '', bgc_amount: '', advance_deduction_amount: '', trace_id: '', notes: '' };
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);
  // POs matching the invoice's WO/period, for attaching a PO to an
  // existing invoice that was created without one. `pos` is passed down
  // as a prop from APInvoicesPage (already fetched once there). (2026-08-18)
  const editFilteredPOs = useMemo(() => {
    if (!editForm?.wo_id || !pos) return [];
    return pos.filter(p => {
      if (p.wo_id !== Number(editForm.wo_id)) return false;
      if (!editForm.period_start || !editForm.period_end) return true;
      return p.period_start <= editForm.period_end && p.period_end >= editForm.period_start;
    });
  }, [pos, editForm?.wo_id, editForm?.period_start, editForm?.period_end]);
  const [saving, setSaving] = useState(false);

  function load() {
    api.get(`/ap-invoices/${invoiceId}`).then(r => setData(r.data)).catch(e => { console.error("Failed to load invoice:", e); setData(null); });
  }

  useEffect(load, [invoiceId]);

  async function savePayment() {
    setSaving(true);
    try {
      const payload = {
        ...payForm,
        cash_amount: Number(payForm.cash_amount || 0),
        epd_rate: payForm.epd_rate ? Number(payForm.epd_rate) : null,
        epd_amount: payForm.epd_amount ? Number(payForm.epd_amount) : 0,
        bgc_amount: payForm.bgc_amount ? Number(payForm.bgc_amount) : 0,
        advance_deduction_amount: payForm.advance_deduction_amount ? Number(payForm.advance_deduction_amount) : 0,
      };
      if (editingPaymentId) {
        await api.put(`/ap-invoices/${invoiceId}/payments/${editingPaymentId}`, payload);
      } else {
        await api.post(`/ap-invoices/${invoiceId}/payments`, payload);
      }
      setPayForm(EMPTY_PAY_FORM);
      setEditingPaymentId(null);
      setShowPayForm(false);
      load();
      onChanged();
    } finally { setSaving(false); }
  }

  function startEditPayment(p) {
    setPayForm({
      payment_date: p.payment_date || '',
      cash_amount: p.cash_amount ?? '',
      epd_rate: p.epd_rate ?? '',
      epd_amount: p.epd_amount ?? '',
      bgc_amount: p.bgc_amount ?? '',
      advance_deduction_amount: p.advance_deduction_amount ?? '',
      trace_id: p.trace_id || '',
      notes: p.notes || '',
    });
    setEditingPaymentId(p.id);
    setShowPayForm(true);
  }

  async function setVerification(paymentId, status) {
    await api.put(`/ap-invoices/${invoiceId}/payments/${paymentId}/verify`, { verification_status: status });
    load();
    onChanged();
  }

  function startEdit() {
    setEditForm({ ...data });
    setIsEditing(true);
    setEditError('');
  }

  async function saveEdit() {
    if (!editForm.invoice_number?.trim()) {
      setEditError('Invoice number required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/ap-invoices/${invoiceId}`, editForm);
      setIsEditing(false);
      setEditForm(null);
      load();
      onChanged();
      toast('Invoice updated', 'success');
    } catch (e) {
      setEditError('Save failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setEditSaving(false);
    }
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 500 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">
            {isEditing ? 'Edit Invoice' : (data?.invoice_number || 'Loading…')}
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isEditing && user?.role === 'admin' && (
              <button className="btn btn-sm btn-secondary" onClick={startEdit}>
                ✏️ Edit
              </button>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/ap-invoices/${invoiceId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {data && !isEditing && (
          <div className="panel-body">
            <div className="panel-summary-line">
              <Badge>{data.status}</Badge>
              <Badge variant={data.invoice_type === 'passthrough' ? 'info' : undefined}>
                {data.invoice_type === 'passthrough' ? 'Allowance / Reimbursement' : 'Standard'}
              </Badge>
              {data.vendor_name} · {data.contractor_name}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 16 }}>
              <DetailRow label="Invoice #" value={data.invoice_number} />
              <DetailRow label="Vendor" value={data.vendor_name} link={data.vendor_id ? { type: 'vendor', id: data.vendor_id } : null} />
              <DetailRow label="Contractor" value={data.contractor_name} link={data.contractor_id ? { type: 'contractor', id: data.contractor_id } : null} />
              <DetailRow label="Invoice Date" value={data.invoice_date} />
              <DetailRow label="Period Start" value={data.period_start} />
              <DetailRow label="Period End" value={data.period_end} />
              <DetailRow label="Hours Billed" value={data.hours_billed} />
              <DetailRow label="Hours Approved" value={data.hours_approved} />
              <DetailRow label="Rate" value={data.rate != null ? `$${data.rate}/hr` : null} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Amount to Pay</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  ${data.amount_to_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  ${data.summary.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <InvoiceNotes notes={data.notes} queueNote={data.queue_note} />

            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Payments</h4>
            {data.payments.length === 0 && (
              <div className="form-hint" style={{ marginBottom: 12 }}>No payments recorded yet.</div>
            )}
            {data.payments.map(p => (
              <div key={p.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong>${p.cash_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  <Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>
                    {p.verification_status}
                  </Badge>
                </div>
                {(p.epd_amount > 0 || p.bgc_amount > 0 || p.advance_deduction_amount > 0) && (
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, marginBottom: 4 }}>
                    {p.epd_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        EPD{p.epd_rate ? ` (${(p.epd_rate * 100).toFixed(0)}%)` : ''}: <strong style={{ color: 'var(--warning)' }}>-${Number(p.epd_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                    {p.bgc_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        BGC: <strong style={{ color: 'var(--warning)' }}>-${Number(p.bgc_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                    {p.advance_deduction_amount > 0 && (
                      <span style={{ color: 'var(--muted)' }}>
                        Advance: <strong style={{ color: 'var(--warning)' }}>-${Number(p.advance_deduction_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                      </span>
                    )}
                  </div>
                )}
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>{p.payment_date}{p.trace_id ? ` · Trace: ${p.trace_id}` : ''}</div>
                <NoteText text={p.notes} />
                {user?.role === 'admin' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'verified')}>
                      ✓ Verify
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'disputed')}>
                      ✗ Dispute
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEditPayment(p)}>
                      ✎ Edit
                    </button>
                  </div>
                )}
              </div>
            ))}

            <button
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: 14 }}
              onClick={() => setShowPayForm(true)}
            >
              + Record Payment
            </button>

            {showPayForm && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
                <h5 style={{ marginBottom: 10, fontSize: 12, fontWeight: 700 }}>{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</h5>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Payment Date</label>
                  <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Cash Amount</label>
                  <input type="number" step="0.01" value={payForm.cash_amount} onChange={e => setPayForm(f => ({ ...f, cash_amount: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div className="form-field">
                    <label>EPD Rate (%)</label>
                    <input type="number" step="0.01" value={payForm.epd_rate} onChange={e => setPayForm(f => ({ ...f, epd_rate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>EPD Amount</label>
                    <input type="number" step="0.01" value={payForm.epd_amount} onChange={e => setPayForm(f => ({ ...f, epd_amount: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>BGC Amount (Background Verification cost)</label>
                  <input type="number" step="0.01" value={payForm.bgc_amount} onChange={e => setPayForm(f => ({ ...f, bgc_amount: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Advance Deduction</label>
                  <input type="number" step="0.01" value={payForm.advance_deduction_amount} onChange={e => setPayForm(f => ({ ...f, advance_deduction_amount: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Bank trace ID</label>
                  <input value={payForm.trace_id} onChange={e => setPayForm(f => ({ ...f, trace_id: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Notes</label>
                  <input value={payForm.notes || ''} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowPayForm(false); setEditingPaymentId(null); setPayForm(EMPTY_PAY_FORM); }}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}
            <DocumentWallet entityType="ap_invoice" entityId={invoiceId} entityName={data?.invoice_number} />
            <AuditLog tableName="ap_invoices" recordId={invoiceId} />
          </div>
        )}

        {editForm && isEditing && (
          <div className="panel-body">
            {editError && (
              <div style={{ background: '#fee', color: '#c33', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div className="form-field">
                <label>Invoice #</label>
                <input type="text" value={editForm.invoice_number || ''} onChange={e => setEditForm(f => ({ ...f, invoice_number: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Invoice Date</label>
                <input type="date" value={editForm.invoice_date || ''} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Period Start</label>
                <input type="date" value={editForm.period_start || ''} onChange={e => setEditForm(f => ({ ...f, period_start: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Period End</label>
                <input type="date" value={editForm.period_end || ''} onChange={e => setEditForm(f => ({ ...f, period_end: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Purchase Order {editForm.po_id ? '' : '(none attached — attach one once Hexaware issues it)'}</label>
                <select value={editForm.po_id || ''} onChange={e => setEditForm(f => ({ ...f, po_id: e.target.value }))}>
                  <option value="">{editFilteredPOs.length ? 'Select PO' : 'No POs match this WO/period'}</option>
                  {editFilteredPOs.map(p => (
                    <option key={p.id} value={p.id}>{p.po_number} ({p.period_start} → {p.period_end})</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Hours Billed</label>
                <input type="number" step="0.01" value={editForm.hours_billed || ''} onChange={e => setEditForm(f => withBilledAmount({ ...f, hours_billed: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Hours Approved</label>
                <input type="number" step="0.01" value={editForm.hours_approved || ''} onChange={e => setEditForm(f => ({ ...f, hours_approved: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Rate ($/hr)</label>
                <input type="number" step="0.01" value={editForm.rate || ''} onChange={e => setEditForm(f => withBilledAmount({ ...f, rate: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Amount to Pay</label>
                <input type="number" step="0.01" value={editForm.amount_to_pay || ''} onChange={e => setEditForm(f => ({ ...f, amount_to_pay: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Invoice Type</label>
                <select value={editForm.invoice_type || 'standard'} onChange={e => setEditForm(f => ({ ...f, invoice_type: e.target.value }))}>
                  <option value="standard">Standard</option>
                  <option value="passthrough">Allowance / Reimbursement</option>
                </select>
              </div>
              <div className="form-field">
                <label>Status</label>
                <select value={editForm.status || 'pending'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                  <option value="void">Void</option>
                </select>
              </div>
              <div className="form-field">
                <label>Notes</label>
                <textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows="3" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={cancelEdit} disabled={editSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {!data && (
          <div className="panel-body">
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value, link }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      {link ? (
        <EntityLink type={link.type} id={link.id} style={{ fontWeight: 600 }}>{value}</EntityLink>
      ) : (
        <span style={{ fontWeight: 600 }}>{value}</span>
      )}
    </div>
  );
}
